/**
 * Utility functions for handling authenticated image URLs
 */

/**
 * Extracts filename from S3 URL or returns the filename if it's already just a filename
 * @param {string} imageUrl - S3 URL or filename
 * @returns {string} - Extracted filename
 */
export const extractFilename = imageUrl => {
  if (!imageUrl) return null

  // If it's already just a filename (no slashes or protocol), return as is
  if (!imageUrl.includes('/') && !imageUrl.includes('://')) {
    return imageUrl.split('?')[0]
  }

  // Extract filename from S3 URL
  // Examples:
  // https://s3.amazonaws.com/bucket/path/filename.png -> filename.png
  // https://s3.amazonaws.com/bucket/assets/images/14.png -> 14.png
  // https://s3.../14.png?X-Amz-Signature=abc -> 14.png
  const urlParts = imageUrl.split('/')
  const lastSegment = urlParts[urlParts.length - 1]
  return lastSegment ? lastSegment.split('?')[0] : null
}

/**
 * Builds the presigned image URL following the same pattern as other API calls
 * @param {string} filename - The image filename
 * @param {string} platform - The platform (default: 'admin')
 * @returns {string} - The presigned URL (relative path like other APIs)
 */
export const buildPresignedImageUrl = (filename, platform = 'admin') => {
  if (!filename) return null
  
  // Follow the same pattern as other API calls - use relative URL
  // Example: /control/study_images?platform=admin&filename=14.png
  return `/control/study_images?platform=${encodeURIComponent(platform)}&filename=${encodeURIComponent(filename)}`
}

const authenticatedImageCache = new Map()

const getAuthenticatedImageCacheKey = (filename, platform) => `${platform}:${filename}`

/**
 * Fetches an image with authentication headers and returns a blob URL.
 * The same filename is requested once per session and reused by every renderer.
 */
export const fetchAuthenticatedImage = async (filename, platform = 'admin', accessToken) => {
  if (!filename || !accessToken) {
    return null
  }

  const cacheKey = getAuthenticatedImageCacheKey(filename, platform)
  if (authenticatedImageCache.has(cacheKey)) {
    return authenticatedImageCache.get(cacheKey)
  }

  const request = (async () => {
    const url = buildPresignedImageUrl(filename, platform)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: accessToken,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch presigned URL: ${response.status}`)
    }

    const data = await response.json()
    const presignedUrl = data?.url
    if (!presignedUrl) {
      throw new Error('No presigned URL in API response')
    }

    const imageResponse = await fetch(presignedUrl)
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch image from S3: ${imageResponse.status}`)
    }

    const blob = await imageResponse.blob()
    return URL.createObjectURL(blob)
  })()

  authenticatedImageCache.set(cacheKey, request)
  request.catch(() => {
    authenticatedImageCache.delete(cacheKey)
  })
  return request
}

